import { DateTime } from "luxon";
import i18n from "../../utils/i18n";
import { getJSON } from "../../utils/request";

const getOverlappedShifts = (shifts, shift) =>
  shifts.filter(
    (other) =>
      // Identity, not a deep comparison: `shift` is always the very object held in
      // state.member.shifts, and the two checks further down already compare this way.
      other !== shift &&
      shift.start_time <= other.end_time &&
      other.start_time <= shift.end_time,
  );

const hasDoubleWorkInCurrentSchedule = (shifts, members, shift) => {
  const overlapped_shifts = getOverlappedShifts(shifts, shift);
  return members.some((member) =>
    overlapped_shifts.some((overlapped_shift) =>
      overlapped_shift.members.some(
        (overlapped_member) => overlapped_member.id === member.id,
      ),
    ),
  );
};

const checkDoubleWorkInAllSchedules = (members, shift) => {
  const member_ids = members.map((member) => member.id);
  const start_time = (
    shift.start_time instanceof DateTime
      ? shift.start_time
      : DateTime.fromISO(shift.start_time)
  ).toISO();
  const end_time = (
    shift.end_time instanceof DateTime
      ? shift.end_time
      : DateTime.fromISO(shift.end_time)
  ).toISO();
  return (
    getJSON(Routes.validate_schedules_path({ format: "json" }), {
      member_ids: member_ids,
      start_time: start_time,
      end_time: end_time,
    })
      // Rejects with the bare message, as $.ajax rejected with the bare statusText: Shift.jsx
      // puts whatever comes out of here straight into an alert(), or into the "skip validation?"
      // confirm. That is also what keeps a failed request visible now that the global ajaxError
      // flash no longer fires for it. Placed before the .then so the double-work message below
      // passes through untouched.
      .catch((error) => {
        throw error.message;
      })
      .then((data) => {
        if (data.is_doubled) {
          throw i18n.t("schedule.shifts.double_work");
        }
        return true;
      })
  );
};

const validate = async (shifts, members, shift) => {
  if (hasDayEveningWorkInCurrentSchedule(shifts, members, shift)) {
    if (shift.day_evening === "day") {
      throw new Error(i18n.t("schedule.shifts.has_day_already"));
    } else {
      throw new Error(i18n.t("schedule.shifts.has_evening_already"));
    }
  }
  if (hasBeforeAfterWorkInCurrentSchedule(shifts, members, shift)) {
    if (shift.before_after === "before") {
      throw new Error(i18n.t("schedule.shifts.has_before_already"));
    } else {
      throw new Error(i18n.t("schedule.shifts.has_after_already"));
    }
  }
  if (hasTooManyHoursInCurrentSchedule(members, shift)) {
    throw new Error(i18n.t("schedule.shifts.above_maximum_hours"));
  }
  if (hasDoubleWorkInCurrentSchedule(shifts, members, shift)) {
    throw new Error(i18n.t("schedule.shifts.double_work"));
  }

  return await checkDoubleWorkInAllSchedules(members, shift);
};

const hasDayEveningWorkInCurrentSchedule = (shifts, members, current_shift) => {
  if (!current_shift.day_evening) {
    return false;
  }
  return shifts.some((other_shift) => {
    if (other_shift === current_shift) return false;
    if (other_shift.day_evening !== current_shift.day_evening) return false;
    return other_shift.members.some((other_member) =>
      members.some((member) => other_member.id === member.id),
    );
  });
};

const hasBeforeAfterWorkInCurrentSchedule = (
  shifts,
  members,
  current_shift,
) => {
  if (!current_shift.before_after) {
    return false;
  }
  return shifts.some((other_shift) => {
    if (other_shift === current_shift) return false;
    if (other_shift.before_after !== current_shift.before_after) return false;
    return other_shift.members.some((other_member) =>
      members.some((member) => other_member.id === member.id),
    );
  });
};

const hasTooManyHoursInCurrentSchedule = (members, current_shift) => {
  const startTime =
    current_shift.start_time instanceof DateTime
      ? current_shift.start_time
      : DateTime.fromISO(current_shift.start_time);
  const endTime =
    current_shift.end_time instanceof DateTime
      ? current_shift.end_time
      : DateTime.fromISO(current_shift.end_time);
  const duration = endTime.diff(startTime, "hours").hours;

  return members.some(
    (member) =>
      member.maximum_shift_hours &&
      member.total_hour_count + duration > member.maximum_shift_hours,
  );
};

const validFor = (shifts, shift, member) => {
  return (
    !hasDayEveningWorkInCurrentSchedule(shifts, [member], shift) &&
    !hasBeforeAfterWorkInCurrentSchedule(shifts, [member], shift) &&
    !hasDoubleWorkInCurrentSchedule(shifts, [member], shift) &&
    !hasTooManyHoursInCurrentSchedule([member], shift)
  );
};

// Default export for backward compatibility
const ShiftValidator = {
  getOverlappedShifts,
  hasDoubleWorkInCurrentSchedule,
  checkDoubleWorkInAllSchedules,
  validate,
  hasDayEveningWorkInCurrentSchedule,
  hasBeforeAfterWorkInCurrentSchedule,
  hasTooManyHoursInCurrentSchedule,
  validFor,
};

export default ShiftValidator;
